'use client';

import { Button } from '@packages/shared/src/ui/Button/Button';
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@packages/shared/src/ui/Popover/Popover';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import {
  infoIconButtonStyles,
  infoIconStyles,
  tooltipArrowStyles,
  tooltipContainerStyles,
  wrapperStyles,
} from './Tooltip.styles';
import type { TooltipProps } from './TooltipProps';
import { useCloseOnPressInCarousel } from './utils/useCloseOnPressInCarousel';

const messageMoreInformation = defineMessage({
  id: 'navigation.tooltip.cms.back.aria-label',
  defaultMessage: 'Weitere Informationen anzeigen',
});

// the positioning names the corner of the info icon, the panel opens towards the opposite side
const panelPlacement: Record<
  NonNullable<TooltipProps['positioning']>,
  Pick<ComponentProps<typeof PopoverContent>, 'side' | 'align'>
> = {
  'top-start': { side: 'bottom', align: 'start' },
  'top-end': { side: 'bottom', align: 'end' },
  'bottom-start': { side: 'top', align: 'start' },
  'bottom-end': { side: 'top', align: 'end' },
  left: { side: 'right', align: 'start' },
  right: { side: 'left', align: 'start' },
};

const Tooltip = ({
  children /* the container the tooltip belongs to */,
  content: tooltipContent,
  variant,
  positioning = 'top-start',
}: TooltipProps) => {
  const positioningInfoIcon = variant === 'inline' ? null : positioning;
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useCloseOnPressInCarousel(isOpen, setIsOpen);

  if (!tooltipContent) {
    return children;
  }

  return (
    <div className={wrapperStyles({ variant, positioning })}>
      {children}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className={infoIconStyles({ variant, positioningInfoIcon })}>
          <PopoverTrigger asChild>
            {/* this is the "I" letter circle background */}
            <Button
              ref={triggerRef}
              aria-label={formatMessage(messageMoreInformation)}
              className={infoIconButtonStyles}
              variant="icon-flat"
              size="small"
            >
              {/* this is the "I" letter only */}
              <span className="icon-emp-info bg-color-info-dark hover:bg-color-info-light" />
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent
          // in inline variant the tooltip should always be beneath the text
          {...panelPlacement[(variant === 'inline' ? 'top-start' : positioning) ?? 'top-start']}
          sideOffset={4}
          collisionPadding={8}
          arrowPadding={8}
          // inside a slider the panel would float over the neighbouring slides
          hideWhenDetached
          className={tooltipContainerStyles}
        >
          {/* eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml */}
          <span dangerouslySetInnerHTML={{ __html: tooltipContent }} />
          <PopoverArrow className={tooltipArrowStyles} width={16} height={8} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

Tooltip.displayName = 'Tooltip';

export { Tooltip };
